import * as React from 'react';
import TailwindBaseModal from '~/UI/Tailwind/TailwindBaseModal';

import { ApiResult, useApi } from '~/infrastructure/mam/ApiHooks';
import type { ErpCountriesWithPriceTypeResponse } from 'com.cogvio.pricemonitor.api';
import { ErpCountriesWithPriceTypeError } from '../api/ErpCountriesWithPriceTypeRequest';
import { ERPRequestApiResult, ERPSessionResponse } from '../api/ERPSessionRequest';

import { formatNumericHumanDate } from '~/DateTime';
import { sortCountries } from '~/MedicinalBrandsAnalytics/Region/reducer';
import ERPPriceVolumeGrowthModalRow, { type ERPPriceVolumeGrowthModalValues } from './ERPPriceVolumeGrowthModalRow';
import { ERPGrowthVolumeRequest } from '../api/ERPGrowthVolumeRequest';
import { ERPInputPriceRequest } from '../api/ERPInputPriceRequest';
import ERPSaveButton from '../ERPSaveButton';
import ERPCancelButton from '../ERPCancelButton';
import { DEFAULT_DECIMAL } from '~/UI/Input/DecimalInput';
import { Success } from '~/infrastructure/mam/Result';
import { Country } from '~/Api/Country';
import ERPModalPriceDotLegend from '~/ERP/ERPModalPriceDotLegend';
import { DistributionType } from '../domain';

export type Props = {
	currency: string;
	erpDate: Date;
	erpCountriesResult: ApiResult<ErpCountriesWithPriceTypeResponse, ErpCountriesWithPriceTypeError>;
	erpSessionResult: ERPRequestApiResult;
	userId: string;
	virtualProductPackName: string;
	onERPSessionReload: () => Promise<void>;
	onClose: () => void;
};

const ERPPriceVolumeGrowthModal = ({
	currency,
	erpDate,
	erpCountriesResult,
	erpSessionResult,
	userId,
	virtualProductPackName,
	onClose,
	onERPSessionReload,
}: Props) => {
	const defaultGrowthVolumeValues = (erpSessionResult.value?.erpSessionDetail.erpSession.countryFigures || []).reduce(
		(aggregator: ERPPriceVolumeGrowthModalValues, data) => ({
			...aggregator,
			[data.country.alpha2]: {
				yearlyVolume: Number(data.yearlyVolume).toFixed(DEFAULT_DECIMAL),
				// NOTE: normalize percentage value
				yoyGrowth: (Number(data.yoyGrowth) * 100).toFixed(DEFAULT_DECIMAL),
				price: undefined,
				isUserDefined: false,
				manuallyChanged: false,
				selectedPrice: null,
				distributionType: DistributionType.RETAIL,
				specialStatus: {
					code: 'no-special-status',
					name: 'No special status',
				},
				isEdited: false,
			},
		}),
		{}
	);

	const valuesWithWithSelectedPrices = (erpSessionResult.value?.erpSessionDetail.inputPrices || []).reduce(
		(aggregator: ERPPriceVolumeGrowthModalValues, data) => ({
			...aggregator,
			[data.country.alpha2]: {
				...(aggregator[data.country.alpha2] || {}),
				selectedPrice: data.selected ? data.id : (aggregator[data.country.alpha2] || {}).selectedPrice || null,
				price: data.price.price.value,
				isUserDefined: (aggregator[data.country.alpha2] || {}).isUserDefined || data.isUserDefined,
				distributionType: data.distributionType as DistributionType,
				isEdited: false,
				specialStatus: data.specialStatus,
			},
		}),
		defaultGrowthVolumeValues
	);

	const pricesByCountry =
		erpSessionResult.value?.erpSessionDetail.erpSession.prices.reduce(
			(aggregator: { [key: string]: ERPSessionResponse['erpSessionDetail']['erpSession']['prices'] }, price) => ({
				...aggregator,
				[price.country.alpha2]: [...(aggregator[price.country.alpha2] || []), price],
			}),
			{}
		) || {};

	const [loading, setLoading] = React.useState(false);
	const [editState, setEditState] = React.useState<ERPPriceVolumeGrowthModalValues>(valuesWithWithSelectedPrices);

	const [fetchRequest] = useApi();

	const disabled = loading;

	const handleSubmit = async () => {
		const inputEntries = Object.entries(editState);
		const hasErrors = inputEntries.some(([_, data]) => data.errors?.size);

		if (disabled || hasErrors || !erpSessionResult.value?.erpSessionDetail.erpPack.id) {
			return;
		}

		setLoading(true);

		const priceData = inputEntries
			.map(([country, { price, selectedPrice, isUserDefined, manuallyChanged, distributionType, isEdited }]) => ({
				country,
				price,
				currency,
				selectedPrice,
				isUserDefined,
				manuallyChanged,
				distributionType,
				isEdited,
			}))
			.filter(({ price, distributionType }) => Boolean(price) || Boolean(distributionType));

		const priceRequest = new ERPInputPriceRequest(
			userId,
			erpSessionResult.value.erpSessionDetail.erpPack.id,
			erpSessionResult.value.erpSessionDetail.erpSession.id,
			priceData
		);

		const volumeGrowthRequest = new ERPGrowthVolumeRequest(
			userId,
			erpSessionResult.value.erpSessionDetail.erpPack.id,
			erpSessionResult.value.erpSessionDetail.erpSession.id,
			Object.entries(editState).map(([countryCode, { yearlyVolume, yoyGrowth }]) => ({
				countryCode,
				yearlyVolume,
				yoyGrowth,
			}))
		);

		const results = await fetchRequest(priceRequest).then((priceResult) =>
			fetchRequest(volumeGrowthRequest).then((volumeResult) => [priceResult, volumeResult])
		);
		const success = results.every((result) => result instanceof Success);

		if (success) {
			await onERPSessionReload();
			setLoading(false);
			onClose();
		} else {
			setLoading(false);
		}
	};

	const isInputValid = Object.values(editState).every(({ errors }) => !errors?.size);

	return (
		<TailwindBaseModal
			footer={
				<>
					<ERPCancelButton onClick={onClose}>Close</ERPCancelButton>
					<ERPSaveButton disabled={disabled || !isInputValid} loading={loading} type='submit'>
						Save &amp; execute model
					</ERPSaveButton>
				</>
			}
			title='Price input, volume & growth'
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<>
				<div className='pb-2'>
					<div className='flex items-start'>
						<div className='py-2 px-2.5 rounded font-display text-label font-semibold text-gray-800 text-center bg-gray-100'>
							{formatNumericHumanDate(erpDate)
								.split(' ')
								.map((word, index) => (
									<span key={index}>
										{index === 2 && <br />}
										{word}
									</span>
								))}
						</div>
						<div className='pl-3'>
							<h2 className='font-display text-md font-semibold text-cPurple-600 tracking-wider'>
								{virtualProductPackName}
							</h2>

							<ERPModalPriceDotLegend />
						</div>
					</div>
				</div>
				<div className='-mx-3'>
					<table cellPadding='0' cellSpacing='0' width='100%'>
						<thead>
							<tr>
								<th className='pl-3 pr-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Country
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Price [{currency}]
								</th>
								<th className='px-1 py-1.5 text-center font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Level
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-labelsmall font-medium text-gray-600 leading-none border-b-2 border-gray-300 w-20'>
									Product
									<br />
									type
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-labelsmall font-medium text-gray-600 leading-none border-b-2 border-gray-300 w-20'>
									Rule
									<br />
									type
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Yearly volume [Nr.]
								</th>
								<th className='pr-3 pl-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									YoY growth [%]
								</th>
							</tr>
						</thead>
						<tbody>
							{sortCountries(erpCountriesResult.value?.items || []).map((country) => (
								<ERPPriceVolumeGrowthModalRow
									availablePrices={pricesByCountry[country.alpha2] || []}
									country={new Country(country.alpha2, country.name)}
									erpDate={erpDate}
									key={country.alpha2}
									priceType={country.priceType}
									values={editState[country.alpha2]}
									onVolumeGrowthChangeRequest={setEditState}
									isHospitalRelevant={country.isHospitalRelevant}
								/>
							))}
						</tbody>
					</table>
				</div>
				<ERPModalPriceDotLegend />
			</>
		</TailwindBaseModal>
	);
};

export default React.memo(ERPPriceVolumeGrowthModal);
